import { getDefaultEcmInfo } from "@/apis/tax";
import { planTypes } from "@/common/constants";
import Enums from "@/common/enums/Enums";
import globalEventBus from "@/common/global-event-bus";
import { US_short } from "@/common/regionUtils";
import { computedCallingPlans } from "@/common/service/computers/plans/pbx";
import store from "@/store";
import { logger } from "@/utils/log";
import QueryString from "qs"
import { translation } from "@/plugins/i18n";

export function getVendorsProducts() {
  try {
    return window.__ZM_BILLING_SHARED?.apis?.getVendorsProducts() || Promise.resolve(null)
  } catch (error) {
    logger.error('getVendorsProducts error', error);
    return Promise.resolve(null)
  }
}

export function getZoomDeviceIdInLink() {
  const params = QueryString.parse(location.search.slice(1));
  return params.dpid
}

export function isBuyVendorLink() {
  return !!getZoomDeviceIdInLink()
}

export function addVendorProduct(allPlans) {
  const dpID = getZoomDeviceIdInLink();
  if (dpID) {
    allPlans.forEach(item => {
      if (item.isVendorProduct) {
        if (item.plan_code == dpID) {
          item.cartInfo.selected_number = 1
        }
      }
    })
  }
  return allPlans
}

export async function injectDevicesParams(params) {
  const getters = store.getters;
  let sellerId = '';
  const deviceList = store
  .getters
  .getSelectedPlans
  .filter(item => item.isVendorProduct)
  .map(item => {
    sellerId = item.__originData?.sellerId;
    return {
      itemId: item.plan_code,
      quantity: item.cartInfo.selected_number
    }
  })
  if (deviceList.length <= 0) return;

  let defaultEcmInfo = null;
  const addressInfo = getters.addressInfo;
  const account = getters.account;
  const countryMap = getters.enCountryMap || getters.countryMap;
  const { country, state, city, address1, zip, phone } = getters.shippingAddressGetter;

  try {
    const { result } = await getDefaultEcmInfo(country, state);
    defaultEcmInfo = result;
  } catch (error) {
    logger.error('getDefaultEcmInfo error', error);
  }

  params.ecommSellerOrderData = {
    address: [{
      firstName: account.firstName,
      lastName: account.lastName,
      companyName: addressInfo.companyName,
      phone: phone || addressInfo.phone,
      country: new Enums(countryMap)[country] || country,
      state: new Enums(US_short)[state] || state,
      city: city,
      address1: address1,
      zip: zip,
    }],
    items: deviceList,
    tax: [{
      sellerId,
      taxExemptStatus: defaultEcmInfo?.exemptStatus || getters.taxInfo.ttrTaxExempt,
      certificateId: defaultEcmInfo?.ecmCertificateId || getters.certificateId,
      ecmExemptionId: defaultEcmInfo?.ecmExemptionId || null,
      createTime: defaultEcmInfo?.createTime || null,
    }]
  }
}

export const DEVICE_VALIDATE_MODAL = {
  EQUAL: 'equal',
  GREATER: 'greater'
}

function deviceValidate(callingSum, deviceSum, modal = DEVICE_VALIDATE_MODAL.EQUAL) {
  if (deviceSum === 0) return true;
  if (modal === DEVICE_VALIDATE_MODAL.EQUAL) {
    return callingSum === deviceSum;
  }
  return callingSum >= deviceSum;
}

export function validateDeviceMaxLicense({ emitMessage = true, deskphoneSum, headsetSum }) {
  if (deskphoneSum > 50) {
    emitMessage && globalEventBus.$emit('desk-phone-max-alert', {
        key: 'desk_phone_max_limit',
        type: 'error',
        title: translation('billing.hw.desk_phones.desk_phone_max_limit'),
        description: translation('billing.hw.desk_phones.desk_phone_max_limit_reason'),
        showIcon: true,
        closable: false
      });
      return Promise.reject();
  }
  if (headsetSum > 50) {
    emitMessage && globalEventBus.$emit('headset-max-alert', {
      key: 'headset_max_limit',
      type: 'error',
      title: translation('billing.hw.headset.max_limit'),
      description: translation('billing.hw.headset.max_limit_reason'),
      showIcon: true,
      closable: false
    });
    return Promise.reject();
  }
}

export function validateDeviceLicense({ emitMessage = true, callingSum, deskphoneSum, headsetSum, modal = DEVICE_VALIDATE_MODAL.EQUAL }) {
  const DEVICE_VALIDATE_MODAL_ALERT_DESKPHONE = {
    [DEVICE_VALIDATE_MODAL.EQUAL]: {
      title: translation('billing.hw.desk_phones.counts_match'),
      description: translation('billing.hw.desk_phones.counts_match_reason'),
    },
    [DEVICE_VALIDATE_MODAL.GREATER]: {
      description: translation('billing.hw.desk_phones.counts_match_calling_bundle'),
    }
  }

  const DEVICE_VALIDATE_MODAL_ALERT_HEADSET = {
    [DEVICE_VALIDATE_MODAL.EQUAL]: {
      title: translation('billing.hw.headset.counts_match'),
      description: translation('billing.hw.headset.counts_match_reason'),
    },
    [DEVICE_VALIDATE_MODAL.GREATER]: {
      description: translation('billing.hw.headset.counts_match_calling_bundle'),
    }
  }

  const DEVICE_VALIDATE_MODAL_ALERT = {
    [DEVICE_VALIDATE_MODAL.EQUAL]: {
      title: translation('billing.hw.counts_match'),
      description: translation('billing.hw.counts_match_reason'),
    },
    [DEVICE_VALIDATE_MODAL.GREATER]: {
      description: translation('billing.hw.counts_match_calling_bundle'),
    }
  }

  const deskphoneValid = deviceValidate(callingSum, deskphoneSum, modal);
  const headsetValid = deviceValidate(callingSum, headsetSum, modal);
  if (!deskphoneValid && !headsetValid) {
    emitMessage && globalEventBus.$emit('device-license-match-alert', {
      key: 'device_license_match',
      type: 'error',
      title: DEVICE_VALIDATE_MODAL_ALERT[modal].title,
      description: DEVICE_VALIDATE_MODAL_ALERT[modal].description,
      showIcon: true,
      closable: false
    });
    return Promise.reject();
  } else if (!deskphoneValid) {
    emitMessage && globalEventBus.$emit('device-license-match-alert', {
      key: 'device_license_match_deskphone',
      type: 'error',
      title: DEVICE_VALIDATE_MODAL_ALERT_DESKPHONE[modal].title,
      description: DEVICE_VALIDATE_MODAL_ALERT_DESKPHONE[modal].description,
      showIcon: true,
      closable: false
    });
    return Promise.reject();
  } else if (!headsetValid) {
    emitMessage && globalEventBus.$emit('device-license-match-alert', {
      key: 'device_license_match_headset',
      type: 'error',
      title: DEVICE_VALIDATE_MODAL_ALERT_HEADSET[modal].title,
      description: DEVICE_VALIDATE_MODAL_ALERT_HEADSET[modal].description,
      showIcon: true,
      closable: false
    });
    return Promise.reject();
  }
  return Promise.resolve();
}

/**
 * This validate function is dangerous, you must handle the promise if you use it.
 * @returns {Promise<void>}
 */
function validateShippingAddress() {
  return new Promise((resolve, reject) => {
    if (!globalEventBus.$has('valid-shipping-form')) return resolve();
    globalEventBus.$emit('valid-shipping-form', { resolve, reject });
  });
}

export async function preSubmitForVendorProduct() {
  const selectedCallingPlans = computedCallingPlans(store.getters.getSelectedPlans);

  const selectedDeskPhones = store.getters.getSelectedPlans.filter(item => item.type === planTypes.ZOOM_DEVICE$DESK_PHONE);
  const deskphoneSum = selectedDeskPhones.reduce((pre, curVal) => (curVal?.cartInfo?.selected_number || 0) + pre, 0)
  const selectedHeadsets = store.getters.getSelectedPlans.filter(item => item.type === planTypes.ZOOM_DEVICE$HEADSET);
  const headsetSum = selectedHeadsets.reduce((pre, curVal) => (curVal?.cartInfo?.selected_number || 0) + pre, 0)
  const callingSum = selectedCallingPlans.reduce((pre, curVal) => ((curVal?.cartInfo?.selected_number || 0) - (curVal?._currentQuantity || 0)) + pre, 0)

  await validateDeviceLicense({
    callingSum, deskphoneSum, headsetSum, modal: DEVICE_VALIDATE_MODAL.GREATER
  });

  await validateDeviceMaxLicense({ deskphoneSum, headsetSum });
  await validateShippingAddress();
}
